// src/components/intentions/FteReconciliationPanel.tsx
//
// Surfaces staff whose recorded FTE disagrees with what they asked for in their
// teaching intention, and lets HR apply the requested figure.
//
// Why this is a reviewed action rather than an automatic sync (issue #226):
// FTE is the denominator of every utilisation figure and feeds load letters.
// Silently overwriting 47 people's contracted capacity from a form submission is
// not something that should happen as a side effect of an import — so the diff is
// shown, and applying is a deliberate click.
//
// The bug this replaces: ImportData only ever wrote FTE when CREATING a staff
// record. Existing staff hit an `else` branch that logged to the console and did
// nothing, so no intention has ever updated an FTE.

import React, { useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, Check, RefreshCw } from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { TeachingIntention } from '../../types/intentions';
import { resolveIntendedFte, sameFte } from '../../utils/intentionFte';

interface FteReconciliationPanelProps {
  intentions: TeachingIntention[];
  /** The year these intentions belong to — shown so nobody applies the wrong year. */
  year: number;
}

interface Mismatch {
  staffId: string;
  staffName: string;
  staffCode: string;
  currentFte: number;
  intendedFte: number;
}

const FteReconciliationPanel: React.FC<FteReconciliationPanelProps> = ({ intentions, year }) => {
  const { staff, updateStaffMember, refreshStaff } = useData();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [applying, setApplying] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<Set<string>>(new Set());

  const { mismatches, unmatched } = useMemo(() => {
    const rows: Mismatch[] = [];
    const missing: string[] = [];

    for (const intention of intentions) {
      const intendedFte = resolveIntendedFte(intention as any);
      if (intendedFte === null) continue;

      const code = (intention.staffId || '').toUpperCase();
      const member = staff.find(
        s => s.status === 'active' && (s.staffCode || '').toUpperCase() === code
      );

      // An intention whose staff_id matches no active staff row can never be
      // applied. Count it rather than dropping it silently.
      if (!member) {
        if (code) missing.push(`${intention.staffName || 'Unknown'} (${code})`);
        continue;
      }

      if (!sameFte(member.fte, intendedFte)) {
        rows.push({
          staffId: member.id,
          staffName: member.name,
          staffCode: member.staffCode || code,
          currentFte: member.fte,
          intendedFte,
        });
      }
    }

    rows.sort((a, b) =>
      Math.abs(b.intendedFte - b.currentFte) - Math.abs(a.intendedFte - a.currentFte)
    );
    return { mismatches: rows, unmatched: missing };
  }, [intentions, staff]);

  const outstanding = mismatches.filter(m => !done.has(m.staffId));

  const applyOne = async (m: Mismatch) => {
    setApplying(prev => new Set(prev).add(m.staffId));
    try {
      await updateStaffMember(m.staffId, { fte: m.intendedFte });
      setDone(prev => new Set(prev).add(m.staffId));
      addToast(`${m.staffName} set to ${m.intendedFte} FTE`, 'success');
    } catch (error) {
      console.error('Failed to apply FTE:', error);
      addToast(`Could not update ${m.staffName}`, 'error');
    } finally {
      setApplying(prev => {
        const next = new Set(prev);
        next.delete(m.staffId);
        return next;
      });
    }
  };

  const applyAll = async () => {
    const ok = await confirm({
      title: `Apply ${outstanding.length} FTE changes?`,
      message:
        `This sets each staff member's recorded FTE to what they requested for ${year}. ` +
        `FTE drives every utilisation figure and feeds load letters. ` +
        `FTE is not stored per year, so this also changes how previous years' ` +
        `utilisation reads.`,
      confirmLabel: `Apply ${outstanding.length} changes`,
      variant: 'primary',
    });
    if (!ok) return;

    let applied = 0;
    let failed = 0;
    for (const m of outstanding) {
      try {
        await updateStaffMember(m.staffId, { fte: m.intendedFte });
        setDone(prev => new Set(prev).add(m.staffId));
        applied++;
      } catch (error) {
        console.error('Failed to apply FTE for', m.staffCode, error);
        failed++;
      }
    }
    await refreshStaff();
    addToast(
      failed === 0
        ? `Applied ${applied} FTE ${applied === 1 ? 'change' : 'changes'}`
        : `Applied ${applied}, failed ${failed} — see console`,
      failed === 0 ? 'success' : 'error'
    );
  };

  if (mismatches.length === 0 && unmatched.length === 0) return null;

  return (
    <Card className="mb-6">
      <div className="p-6 border-b border-ink-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-card-title text-ink-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warn-fg" />
            FTE not yet applied
          </h3>
          <p className="mt-1 text-sm text-ink-500">
            <span className="font-mono tabular-nums">{outstanding.length}</span> staff have a{' '}
            <span className="font-mono tabular-nums">{year}</span> intention whose FTE differs from
            their staff record. Nothing applies these automatically.
          </p>
        </div>
        {outstanding.length > 0 && (
          <Button variant="secondary" onClick={applyAll} leftIcon={<RefreshCw className="h-4 w-4" />}>
            Apply all ({outstanding.length})
          </Button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-ink-200">
          <thead className="bg-ink-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-ink-500 uppercase">Staff</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-ink-500 uppercase">Code</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-ink-500 uppercase">Recorded</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-ink-500 uppercase">Requested</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-ink-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-ink-200">
            {mismatches.map(m => {
              const isDone = done.has(m.staffId);
              const isApplying = applying.has(m.staffId);
              return (
                <tr key={m.staffId} className={isDone ? 'bg-ok-bg' : undefined}>
                  <td className="px-4 py-2 text-sm text-ink-900">{m.staffName}</td>
                  <td className="px-4 py-2 text-sm font-mono tabular-nums text-ink-700">{m.staffCode}</td>
                  <td className="px-4 py-2 text-sm text-right font-mono tabular-nums text-ink-700">
                    {m.currentFte.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-sm text-right font-mono tabular-nums text-ink-900 font-semibold">
                    {m.intendedFte.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {isDone ? (
                      <span className="inline-flex items-center gap-1 text-sm text-ok-fg">
                        <Check className="h-4 w-4" /> Applied
                      </span>
                    ) : (
                      <button
                        onClick={() => applyOne(m)}
                        disabled={isApplying}
                        className="inline-flex items-center gap-1 text-sm text-navy-600 hover:text-navy-800 disabled:opacity-50"
                      >
                        {isApplying ? 'Applying…' : <>Apply <ArrowRight className="h-3 w-3" /></>}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {unmatched.length > 0 && (
        <div className="px-6 py-3 border-t border-ink-200 text-sm text-ink-500">
          <span className="font-medium text-ink-700">{unmatched.length}</span> intention
          {unmatched.length === 1 ? '' : 's'} could not be matched to an active staff record and
          {unmatched.length === 1 ? ' is' : ' are'} not listed above: {unmatched.join(', ')}.
        </div>
      )}
    </Card>
  );
};

export default FteReconciliationPanel;
